/**
 * jm:cart — UMA fonte de estado do carrinho para a loja inteira.
 *
 * O problema que ele resolve
 * -------------------------
 * A gaveta, a página /cart, o banner e a barra de progresso precisam dos mesmos
 * dois números: quantas peças ELEGÍVEIS o carrinho tem (que mandam na faixa e
 * no desconto) e quantas peças FÍSICAS ele tem (que mandam no mínimo, e nas
 * quais a PROMOÇÃO conta). Se cada um consultar
 * `/cart.js` por conta própria, a loja passa a ter quatro respostas para a
 * mesma pergunta — e elas divergem, porque chegam em momentos diferentes. Foi
 * exatamente esse tipo de divergência que a Fonte Única veio acabar, do lado do
 * dinheiro; aqui é a mesma doença, do lado da tela.
 *
 * Então existe um consultor só. Ele percebe quando o carrinho mudou, busca
 * `/cart.js` UMA vez, calcula o estado pela Fonte Única e avisa quem quiser
 * ouvir:
 *
 *     document.addEventListener('jm:cart',      (e) => { ... e.detail ... });
 *     document.addEventListener('jm:cart:erro', (e) => { ... e.detail.motivo });
 *
 * Como ele percebe a mudança
 * --------------------------
 * Envolvendo `fetch` e `XMLHttpRequest`. Não é elegante, mas é o único jeito de
 * saber que o carrinho mudou sem perguntar de tempos em tempos — e perguntar de
 * tempos em tempos é pior: gasta bateria, chega atrasado e ainda erra quando o
 * cliente é rápido. Não há `setInterval` aqui.
 *
 * ZERO POR FALHA NÃO É ZERO DE VERDADE
 * ------------------------------------
 * Esta é a lição que custou caro. A versão anterior lia o estado com
 * `caixa.getElementById(...)`, onde `caixa` era um `<div>` solto. `getElementById`
 * só existe em `Document` e `DocumentFragment`, nunca em `Element`: a chamada
 * lançava `TypeError`, um `.catch` calado devolvia `null`, e o módulo caía num
 * estado antigo — o da primeira carga, com o carrinho vazio. A barra passou a
 * dizer "zero elegíveis" em TODO carrinho, e parecia um problema de rede.
 *
 * Por isso, agora: nenhum `catch` engole erro estrutural, o estado desenhado na
 * página só é reaproveitado quando bate com o `/cart.js` de agora, e quando não
 * dá para saber o módulo diz que não sabe — em vez de inventar zero.
 *
 * Quem calcula o desconto é `JMFaixas`, que lê o metacampo `custom.faixas_desconto`.
 * Este arquivo não conhece nenhuma faixa, nenhum mínimo e nenhum valor.
 */
(function (global) {
  "use strict";

  var CAMINHOS = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var ultimo = null;
  var pedido = null;

  /**
   * O estado do carrinho como o Liquid o vê — com a elegibilidade de cada linha
   * já decidida no servidor, onde a pergunta "este produto está numa coleção
   * excluída?" tem resposta exata.
   *
   * Fazer essa conta no navegador exigiria a lista dos produtos excluídos, e
   * essa lista não cabe: o Liquid entrega no máximo 50 produtos por coleção, e
   * as duas PROMOÇÃO somam 297. Metade ficaria de fora e passaria a contar como
   * elegível.
   *
   * `raiz` pode ser o documento ou um elemento qualquer. `querySelector` existe
   * nos dois; `getElementById`, não.
   */
  function estadoDoLiquid(raiz) {
    var el = (raiz || document).querySelector("#jm-carrinho-estado");
    if (!el) return null;
    return JSON.parse(el.textContent);       /* JSON quebrado é defeito nosso:
                                                estoure, não devolva null */
  }

  /** Quantas peças o estado do Liquid contém ao todo — elegíveis ou não. */
  function totalDoEstado(e) {
    var n = 0;
    ((e && e.itens) || []).forEach(function (i) { n += i.quantidade | 0; });
    return n;
  }

  function avisarErro(motivo, erro) {
    document.dispatchEvent(new CustomEvent("jm:cart:erro", {
      detail: { motivo: motivo, erro: erro || null }
    }));
  }

  /**
   * A linha do carrinho na linguagem do `JMFaixas` — SEM decidir nada.
   *
   * Esta função já traduziu um booleano vindo do Liquid numa coleção INVENTADA
   * (`colecoes: ["excluida"]`), com `tags`, `valePresente` e `emPromocao`
   * zerados. Era o ponto onde a informação morria: o motor sabe avaliar treze
   * campos, e recebia um. Quem decidia era o Liquid, com dois.
   *
   * Agora repassa o que o Liquid apurou de verdade. Nada aqui interpreta a
   * regra — interpretar é trabalho do `JMFaixas.elegivel`, e de mais ninguém.
   */
  function paraFaixas(item) {
    return {
      id: String(item.chave),
      quantidade: item.quantidade | 0,
      precoCentavos: item.precoCentavos | 0,
      produtoId: String(item.produto),
      varianteId: String(item.variante),
      colecoes: item.colecoes || [],
      tags: item.tags || [],
      tipoProduto: item.tipoProduto || "",
      fornecedor: item.fornecedor || "",
      valePresente: !!item.valePresente,
      emPromocao: !!item.emPromocao
    };
  }

  /**
   * O estado do carrinho, com a elegibilidade decidida pelo MOTOR.
   *
   * Este arquivo já contou as peças elegíveis por conta própria, somando as
   * linhas que o Liquid marcara como elegíveis. Eram duas contas para a mesma
   * pergunta, e bastava uma delas mudar para a loja passar a ter duas respostas.
   * Agora a contagem é `qtdElegivel`, que sai do mesmo `JMFaixas.calcular` que
   * decide o desconto — o número e o dinheiro nascem da MESMA passada.
   *
   * Sem motor ou sem configuração, devolve `null`. NÃO devolve zero: zero é uma
   * afirmação sobre o carrinho, e aqui não se sabe nada. Quem chama transforma
   * isso em `jm:cart:erro`.
   */
  function calcular(cart, doLiquid) {
    var F = global.JMFaixas;
    var cfg = F && F.config ? F.config() : null;
    if (!F || !cfg) return null;

    var itens = (doLiquid && doLiquid.itens) || [];
    var linhas = itens.map(paraFaixas);

    /* O cliente vem do Liquid, e `null` quando o visitante é anônimo. É o
       motor quem decide o que fazer com isso — aqui só se repassa. */
    var r = F.calcular(linhas, cfg, { cliente: (doLiquid && doLiquid.cliente) || null });
    var qtd = r.qtdElegivel | 0;
    var proxima = F.proximaFaixa(qtd, cfg);
    var minimo = (doLiquid && doLiquid.minimo) || F.minimoPecas(cfg);

    /* O MÍNIMO CONTA PEÇA FÍSICA (17/08/2026).
       Até hoje quem mandava no mínimo era `qtd`, o elegível — e isso trancava a
       porta na cara de quem montasse um carrinho só de PROMOÇÃO: três peças na
       caixa, zero elegíveis, "faltam 3 peças". Pedido legítimo, botão desligado.
       Decisão do dono: a PROMOÇÃO conta para ABRIR a porta, e segue não contando
       para o DESCONTO — ela já tem o preço dela, igual em qualquer quantidade.
       Vale-presente continua fora: não é peça de roupa, e o cliente que só o
       levasse no carrinho não estaria fazendo um pedido de atacado. */
    var fisicas = 0;
    itens.forEach(function (i) {
      if (!i.valePresente) fisicas += i.quantidade | 0;
    });

    return {
      cart: cart,
      totalNoCarrinho: cart.item_count | 0,
      elegiveis: qtd,
      fisicas: fisicas,
      minimo: minimo,
      faltamParaMinimo: Math.max(0, minimo - fisicas),
      liberado: fisicas >= minimo,
      faixa: r.faixa || null,
      descontoCentavos: r.descontoCentavos | 0,
      proxima: proxima,                       // { faixa, faltam } ou null
      melhorPreco: qtd >= minimo && !proxima,
      config: cfg
    };
  }

  function avisar(cart, doLiquid) {
    var estado = calcular(cart, doLiquid);
    if (!estado) {
      /* Sem motor ou sem regra publicada não se avisa "zero elegíveis": zero é
         uma afirmação, e aqui não se sabe nada. Quem ouve `jm:cart:erro` se
         cala; quem ouve `jm:cart` nunca recebe um número inventado. */
      avisarErro("JMFaixas indisponivel ou sem configuracao publicada");
      return;
    }
    ultimo = estado;
    global.JMCarrinho = estado;               // para quem chegar depois do evento
    document.dispatchEvent(new CustomEvent("jm:cart", { detail: estado }));
  }

  /**
   * O estado recém-renderizado pelo Liquid, com a elegibilidade de cada linha.
   *
   * Devolve o estado, ou `null` quando a seção respondeu mas não trouxe o
   * bloco esperado. Erro de rede sobe — quem chamou decide o que fazer, e o que
   * ele NÃO pode fazer é fingir que o carrinho está vazio.
   */
  function buscarEstado() {
    var atual = estadoDoLiquid(document);
    var id = atual && atual.secao;
    if (!id) return Promise.resolve(null);
    return fetch("/?sections=" + encodeURIComponent(id),
                 { headers: { Accept: "text/html" } })
      .then(function (r) {
        if (!r.ok) throw new Error("secao respondeu " + r.status);
        return r.json();
      })
      .then(function (j) {
        var html = j && (j[id] || j[Object.keys(j)[0]]);
        if (!html) return null;
        /* `innerHTML` num elemento solto: aqui `getElementById` NÃO existe —
           foi essa a linha que quebrou tudo. `querySelector` existe. */
        var caixa = document.createElement("div");
        caixa.innerHTML = html;
        return estadoDoLiquid(caixa);
      });
  }

  /** Uma busca por rajada de mudanças: dez cliques rápidos não viram dez GETs. */
  function atualizar(atraso) {
    clearTimeout(pedido);
    pedido = setTimeout(function () {
      var carrinho = fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) {
          if (!r.ok) throw new Error("/cart.js respondeu " + r.status);
          return r.json();
        });

      Promise.all([carrinho, buscarEstado()]).then(function (r) {
        var cart = r[0];
        var fresco = r[1];

        if (fresco) { avisar(cart, fresco); return; }

        /* A seção não respondeu. O estado desenhado na página SÓ serve se ainda
           descrever este carrinho — e a prova disso é a contagem bater com o
           `/cart.js` de agora. Se não bater, ele é de outro instante, e usá-lo
           é inventar um número. */
        var daPagina = estadoDoLiquid(document);
        if (daPagina && totalDoEstado(daPagina) === (cart.item_count | 0)) {
          avisar(cart, daPagina);
          return;
        }
        avisarErro("a secao nao respondeu e o estado da pagina esta velho "
                   + "(pagina: " + totalDoEstado(daPagina) + " pecas, "
                   + "carrinho: " + (cart.item_count | 0) + ")");
      }).catch(function (e) {
        /* Erro de verdade: de rede, de JSON, de programação. Ele aparece —
           não vira "carrinho vazio". */
        avisarErro((e && e.message) || String(e), e);
      });
    }, atraso === undefined ? 220 : atraso);
  }

  // ---- percebe as mudanças ------------------------------------------------
  var fetchOriginal = global.fetch;
  if (fetchOriginal) {
    global.fetch = function (entrada, opcoes) {
      var url = typeof entrada === "string" ? entrada : (entrada && entrada.url) || "";
      return fetchOriginal.apply(this, arguments).then(function (resposta) {
        if (CAMINHOS.test(url) && resposta.ok) atualizar();
        return resposta;
      });
    };
  }

  var abrirOriginal = global.XMLHttpRequest && global.XMLHttpRequest.prototype.open;
  if (abrirOriginal) {
    global.XMLHttpRequest.prototype.open = function (metodo, url) {
      this.addEventListener("load", function () {
        if (CAMINHOS.test(String(url)) && this.status >= 200 && this.status < 300) {
          atualizar();
        }
      });
      return abrirOriginal.apply(this, arguments);
    };
  }

  // páginas que mexem no carrinho por formulário e recarregam
  document.addEventListener("submit", function (e) {
    var acao = (e.target && e.target.getAttribute("action")) || "";
    if (CAMINHOS.test(acao)) atualizar(600);
  }, true);

  // primeira carga e volta pelo histórico
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { atualizar(0); });
  } else {
    atualizar(0);
  }
  global.addEventListener("pageshow", function (e) { if (e.persisted) atualizar(0); });

  global.JMCarrinhoEvento = {
    atualizar: atualizar,
    ultimo: function () { return ultimo; },
    calcular: calcular,
    // expostos para as provas do ambiente de demonstração
    _estadoDoLiquid: estadoDoLiquid,
    _buscarEstado: buscarEstado
  };
})(window);
